<div class="col-12 col-xl-8">
  <div class="entity-binding-workspace bg-white bg-opacity-75 rounded-4 border shadow p-3 h-100">
    <div class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-1">
      <div>
        <p class="fw-bold mb-1 text-x">Active SLA Bindings</p>
        <div class="entity-panel-caption text-xs fw-semibold">
          Bind one or more SLA policies to this entity configuration.
        </div>
      </div>
      <span class="entity-detail-pill entity-status-active rounded-pill fw-bold text-uppercase">
        {{ bound_slas_count }} Active
      </span>
    </div>

    <div class="entity-new-binding-card bg-white rounded-4 border p-3 mb-3 mt-3 shadow">
      <div class="d-flex align-items-start gap-2 mb-3">
        <div class="entity-new-binding-icon rounded-3 d-inline-flex align-items-center justify-content-center">
          <i class="bi bi-link-45deg"></i>
        </div>
        <div>
          <div class="fw-bold text-xs mb-1">Add SLA Binding(s)</div>
          <div class="entity-panel-caption text-xxs fw-semibold">
            Select one or more SLA policies to bind to this entity setup.
          </div>
        </div>
      </div>

      <div class="row g-3 align-items-end">
        <div class="col-12 col-lg-9">
          <label class="form-label mb-1 fw-semibold text-xs">Select SLA</label>
          <div ref="slaMultiselect" class="template-multiselect position-relative">
            <button
              type="button"
              class="form-select subsystem-select entity-binding-control rounded-small d-flex align-items-center justify-content-between gap-2 p-2 shadow-sm"
              :disabled="slas_loading || creating_sla_binding"
              @click="toggle_sla_dropdown"
            >
              <span class="text-truncate">{{ selected_slas_label }}</span>
              <i class="bi bi-chevron-down small"></i>
            </button>

            <div v-if="sla_dropdown_open" class="template-multiselect-menu rounded-small shadow-sm mt-1 p-2">
              <div v-if="slas_loading" class="px-2 py-1 small text-secondary">Loading SLAs...</div>
              <div v-else-if="!bindable_slas.length" class="px-2 py-1 small text-secondary">No SLA policies available</div>
              <div v-else class="template-multiselect-items">
                <label
                  v-for="sla in bindable_slas"
                  :key="sla.id"
                  class="template-multiselect-item d-flex align-items-center gap-2 px-2 py-1 rounded-small"
                >
                  <input
                    class="form-check-input mt-0"
                    type="checkbox"
                    :value="String(sla.id)"
                    v-model="selected_sla_ids"
                    :disabled="creating_sla_binding"
                  />
                  <span class="small text-truncate">{{ sla.name }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-3">
          <button
            type="button"
            class="btn entity-create-binding-btn rounded-small shadow-sm px-3 py-2 fw-semibold border-0 text-white w-100"
            :disabled="creating_sla_binding || !selected_sla_ids.length"
            @click="add_sla_bindings"
          >
            <i class="bi bi-link-45deg me-2"></i>
            {{ creating_sla_binding ? 'Binding...' : 'Bind SLA(s)' }}
          </button>
        </div>
      </div>
    </div>

    <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
      <div class="fw-bold small">Current SLA Bindings</div>
    </div>

    <div class="d-flex flex-column gap-2">
      <div
        v-for="binding in sla_bindings"
        :key="binding.id"
        class="entity-binding-list-card bg-white d-flex align-items-start justify-content-between gap-3 rounded-4 p-3 shadow-sm"
      >
        <div class="d-flex align-items-start gap-3 flex-grow-1 overflow-hidden">
          <div
            class="entity-summary-icon mt-2 ms-2 rounded-3 d-inline-flex align-items-center justify-content-center flex-shrink-0 align-self-stretch"
          >
            <i class="bi bi-bell fs-4"></i>
          </div>
          <div class="flex-grow-1 overflow-hidden">
            <div class="fw-bold small text-truncate mb-1">{{ binding.name || 'SLA Policy' }}</div>
            <div v-if="binding.description" class="entity-panel-caption text-xxs text-truncate">
              {{ binding.description }}
            </div>

            <div class="d-flex flex-wrap gap-2 mt-2">
              <span class="badge rounded-pill text-bg-light border text-secondary text-xxs fw-semibold">
                Target: {{ binding.target_duration_label }}
              </span>
              <span class="badge rounded-pill text-bg-light border text-secondary text-xxs fw-semibold">
                Warning: {{ binding.warning_threshold_label }}
              </span>
              <span class="badge rounded-pill text-bg-light border text-secondary text-xxs fw-semibold">
                Escalation: {{ binding.escalation_duration_label }}
              </span>
            </div>
          </div>
        </div>

        <div class="d-flex flex-column align-items-end gap-2 flex-shrink-0">
          <button
            type="button"
            class="btn btn-link btn-sm p-0 text-danger text-decoration-none small fw-semibold d-inline-flex align-items-center justify-content-end gap-2 text-nowrap"
            :disabled="removing_binding_id === binding.id"
            @click="remove_sla_binding(binding)"
          >
            <i class="bi bi-trash3"></i>
            {{ removing_binding_id === binding.id ? 'Removing...' : 'Remove' }}
          </button>
        </div>
      </div>

      <div v-if="!sla_bindings.length" class="entity-activity-card rounded-4 p-3 small text-secondary bg-white">
        No SLA bindings yet. Add an SLA above.
      </div>
    </div>
  </div>
</div>

<div class="col-12 col-xl-4">
  <div class="entity-overview-profile-card rounded-4 border shadow p-4 h-100 overflow-hidden">
    <h2 class="entity-overview-title fw-bold mb-3">SLA Context</h2>
    <div class="small mb-4">
      <div class="entity-panel-caption fw-semibold">Binding SLAs for</div>
      <div class="fw-bold">{{ entityTypeName || 'Entity' }}</div>
    </div>

    <div class="d-flex flex-column gap-3 my-4">
      <div class="d-flex align-items-center justify-content-between gap-2">
        <span class="small text-secondary">Available SLAs</span>
        <span class="small fw-bold">{{ available_slas_count }}</span>
      </div>
      <div class="d-flex align-items-center justify-content-between gap-2">
        <span class="small text-secondary">Bound SLAs</span>
        <span class="small fw-bold">{{ bound_slas_count }}</span>
      </div>
    </div>

    <button
      type="button"
      class="btn entity-overview-profile-btn rounded-small w-100 text-xs fw-semibold"
      @click="go_to_sla_overview"
    >
      <i class="bi bi-bell me-2"></i>
      View SLA Library
    </button>
  </div>
</div>
